import { Popup } from 'components/Popup'
import { BUTTON_TYPES, Button } from 'components/Button'
import { BTN_TXT, PLACEHOLDERS, VALIDATION_MESSAGES } from 'constants/txt'
import { useFinancialsPopup } from 'features/Financials/Providers/FinancialsPopupProvider'
import { useCallback, useState, useEffect } from 'react'
import { useDispatch } from 'react-redux'
import useRouter from 'hooks/useRouter'
import { routes } from 'router/Config/config.routes'
import { TYPOGRAPHY_WEIGHT, Typography } from 'components/Typography'
import { createRebatesPayment, fetchPaymentOptions } from './api'
import { Select } from 'components/Select'
import { TOptionWithSubtitle } from 'components/Select/types'
import { formatBankAccounName } from 'features/BankAccount/helpers'
import { Collapse } from 'components/Collapse'
import { Collapse as CollapseAntd, Form } from 'antd'
import { ReactComponent as ArrowDown16 } from 'assets/svg/ArrowDown16.svg'
import { CustomForm } from 'components/Forms/CustomForm'
import { formatAmount, validateMessages } from 'helper/common'
import { setLoading } from 'redux/store/common/slice'
import { notification } from 'components/Notification'
import { useFinancialsRebatesContext } from 'features/Financials/Providers/FinancialsRebatesProvider'
import cn from 'classnames'
import './styles.scss'
import { AxiosResponse } from 'axios'

const { Panel } = CollapseAntd

const FORM_ID = 'initiate-rebates-payment-form'

type TFormValues = {
  bank_account: string
}

type TProps = {
  onResetSelection: () => void
}

export const InitiateRebatesPaymentPopup = ({ onResetSelection }: TProps) => {
  const {
    initiateRebatesPaymentPopup: { actions, state }
  } = useFinancialsPopup()

  const { row, rebatePaymentData } = state.payload
  const dispatch = useDispatch()
  const { navigate } = useRouter()
  const [form] = Form.useForm<TFormValues>()
  const {
    actions: { updateRebatesData }
  } = useFinancialsRebatesContext()

  const [paymentOptions, setPaymentOptions] = useState<TOptionWithSubtitle[]>(
    []
  )
  const firstOptionValue = paymentOptions[0]?.value
  const selectedBankAccount = Form.useWatch('bank_account', form)

  const onCancel = useCallback(() => {
    form.resetFields()
    actions.close()
  }, [actions, form])

  const onPay = useCallback(
    async ({ bank_account }: TFormValues) => {
      if (!row?.uuid || !bank_account) return
      try {
        dispatch(setLoading(true))
        await createRebatesPayment(row.uuid, {
          vendor_payable_bank_account_id: bank_account
        })
        notification.success({
          message: VALIDATION_MESSAGES.REBATES_PAYMENT_WAS_SUBMITTED
        })
        form.resetFields()
        actions.close()
        onResetSelection()
        await updateRebatesData()
      } catch (error) {
        const typedResponse = error as AxiosResponse
        Object.values(typedResponse.data).forEach((error) => {
          notification.error({ message: error as string })
        })
      } finally {
        dispatch(setLoading(false))
        actions.close()
      }
    },
    [actions, dispatch, form, onResetSelection, row?.uuid, updateRebatesData]
  )

  const getPaymentOptions = useCallback(async (businessAccountId: string) => {
    const {
      data: { results }
    } = await fetchPaymentOptions(businessAccountId)
    setPaymentOptions(
      results.map((account) => ({
        label: formatBankAccounName(account),
        subtitle: account.bank_name,
        value: account.uuid
      }))
    )
  }, [])

  const goToBankAccounts = useCallback(() => {
    actions.close()
    navigate(
      `${routes.myCommunity}${routes.myOrganization}${routes.bankAccounts}`
    )
  }, [actions, navigate])

  useEffect(() => {
    if (rebatePaymentData?.vendor_data.business_account_id) {
      getPaymentOptions(rebatePaymentData?.vendor_data.business_account_id)
    }
  }, [getPaymentOptions, rebatePaymentData])

  useEffect(() => {
    if (state.visible && firstOptionValue) {
      form.setFieldsValue({
        bank_account: String(firstOptionValue)
      })
    }
  }, [firstOptionValue, form, state.visible])

  if (!row || !rebatePaymentData) return null

  return (
    <>
      <Popup
        {...state}
        footer={
          <>
            <Button onClick={onCancel} type={BUTTON_TYPES.DEFAULT} upperCase>
              {BTN_TXT.CANCEL}
            </Button>
            <Button
              type={BUTTON_TYPES.PRIMARY}
              htmlType="submit"
              form={FORM_ID}
              upperCase
              disabled={!selectedBankAccount}
            >
              {BTN_TXT.CONFIRM_AND_PAY}
            </Button>
          </>
        }
      >
        <CustomForm<TFormValues>
          form={form}
          id={FORM_ID}
          onFinish={onPay}
          validateMessages={validateMessages}
        >
          <Typography.Body2 className="grey-text">
            to {row.hospital_name}
          </Typography.Body2>
          <div className="column gap-16 mt-16">
            <div className="column">
              <Typography.Body1 weight={TYPOGRAPHY_WEIGHT.SEMI_BOLD}>
                Payable account
              </Typography.Body1>
              <Typography.Body2 className="grey-text">
                Manage connected accounts in{' '}
                <Button type={BUTTON_TYPES.LINK} onClick={goToBankAccounts}>
                  Bank accounts
                </Button>
              </Typography.Body2>
            </div>
            <Select.WithSubtitle
              options={paymentOptions}
              propsItem={{
                name: 'bank_account',
                rules: [
                  {
                    required: true
                  }
                ]
              }}
              propsSelect={{
                placeholder: PLACEHOLDERS.PLEASE_SELECT
              }}
              alwaysName
            />
            <div className="invoice-summary-collapse">
              <Collapse
                defaultActiveKey="invoice-summary"
                accordion={false}
                expandIcon={(panelProps) => (
                  <span
                    className={cn('invoice-summary-collapse__icon', {
                      'invoice-summary-collapse__icon--collapsed':
                        !panelProps.isActive
                    })}
                  >
                    <ArrowDown16 />
                  </span>
                )}
              >
                <Panel
                  key="invoice-summary"
                  header={`Invoice summary (${rebatePaymentData.invoices.length})`}
                >
                  <div className="invoice-summary-collapse__list column gap-8">
                    {rebatePaymentData.invoices.map((invoice) => (
                      <div
                        key={invoice.supplier_invoice_number}
                        className="row space-between align-center"
                      >
                        <Typography.Body2>
                          {invoice.supplier_invoice_number}
                        </Typography.Body2>
                        <Typography.Body2>
                          {formatAmount(invoice.rebate_amount)}
                        </Typography.Body2>
                      </div>
                    ))}
                  </div>
                </Panel>
              </Collapse>
              <div className="invoice-summary-collapse__footer column gap-12">
                <div className="invoice-summary-collapse__divider" />
                <div className="column gap-8">
                  <div className="row space-between align-center">
                    <Typography.Body2>
                      OM Community processing fee
                    </Typography.Body2>
                    <Typography.Body2>
                      {formatAmount(rebatePaymentData.processing_fee)}
                    </Typography.Body2>
                  </div>
                  <div className="row space-between align-center">
                    <Typography.Body1 weight={TYPOGRAPHY_WEIGHT.SEMI_BOLD}>
                      Total due
                    </Typography.Body1>
                    <Typography.Body1 weight={TYPOGRAPHY_WEIGHT.SEMI_BOLD}>
                      {formatAmount(rebatePaymentData.rebate_amount_pre_cf)}
                    </Typography.Body1>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </CustomForm>
      </Popup>
    </>
  )
}
